/**
 * Voxly — native push notifications bridge.
 *
 * Lifecycle:
 *   1. App boots → user logs in → `registerPushNotifications()` is called.
 *   2. We request permission, register with the OS, then POST the token to
 *      `/api/devices/register` so the backend can later target this device.
 *   3. When the user logs out, `unregisterPushNotifications()` clears the token
 *      on the server (so a stranger logging into the same device doesn't get
 *      the previous user's pushes).
 *
 * On a web build, every call is a safe no-op.
 */
import { api } from "@/lib/api";
import { isNative, platform } from "@/lib/native";

let _lastToken = null;
let _wired = false;

export async function registerPushNotifications() {
  if (!isNative()) return { ok: false, reason: "web" };
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");

    // Ask the OS for permission. iOS shows the system prompt the first time.
    const perm = await PushNotifications.requestPermissions();
    if (perm.receive !== "granted") {
      return { ok: false, reason: "denied" };
    }

    if (!_wired) {
      _wired = true;
      PushNotifications.addListener("registration", async (token) => {
        _lastToken = token?.value;
        if (!_lastToken) return;
        try {
          await api.post("/devices/register", {
            token: _lastToken,
            platform: platform(),
            app_version: process.env.REACT_APP_VERSION || null,
            locale: typeof navigator !== "undefined" ? navigator.language : null,
          });
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn("[push] backend register failed", e?.message || e);
        }
      });

      PushNotifications.addListener("registrationError", (err) => {
        // eslint-disable-next-line no-console
        console.warn("[push] OS registration error", err);
      });

      // Foreground delivery — surface a toast / refresh notifications list.
      PushNotifications.addListener("pushNotificationReceived", () => {
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("voxly:push-received"));
        }
      });

      // User tapped a notification while app was backgrounded.
      PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
        const data = action?.notification?.data || {};
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("voxly:push-tapped", { detail: data })
          );
        }
      });
    }

    await PushNotifications.register();
    return { ok: true };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[push] register failed", e);
    return { ok: false, reason: "exception" };
  }
}

export async function unregisterPushNotifications() {
  if (!isNative() || !_lastToken) return;
  try {
    await api.delete(`/devices/${encodeURIComponent(_lastToken)}`);
  } catch {
    /* noop — user is logging out anyway */
  }
  _lastToken = null;
}
